/**
 * TestRunRedirect — resolves a `/runs/tests/$testRunId` link to the canonical
 * run detail page (`/runs/$runId`).
 *
 * The per-test detail UI used to live here, but the run detail page is now the
 * single investigation surface. This route is kept only as a thin resolver so
 * the many inbound links that know a testRunId (but not a workflowRunId) still
 * work: once the test has produced a workflow run we redirect there, carrying
 * the testRunId as context. While the run is still spinning up we poll (the
 * data hook refetches queued/running rows), and terminal tests that never
 * produced a run show a small note instead of a blank page.
 *
 * @module ui/pages/TestRunRedirect
 */

import React from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { FilmSlate, FlowArrow } from '@phosphor-icons/react';
import { Page, PageHeader, Text, Button, LoadingOrbit, EmptyState } from '@chatsdet/ui';
import { OrgSwitchPrompt } from '../components/OrgSwitchPrompt';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { useTestRunDetail } from '../../features/workflows/useTestRunDetail';

export const TestRunRedirect: React.FC = () => {
  const navigate = useNavigate();
  const { testRunId } = useParams({ from: '/runs/tests/$testRunId' });

  const { wrongOrgInfo, clearWrongOrgInfo, ...testRunQuery } = useTestRunDetail(testRunId);
  const testRun = testRunQuery.data?.testRun;
  const workflowRun = testRunQuery.data?.workflowRun;

  useBreadcrumbs([{ label: 'Runs', onPress: () => navigate({ to: '/runs' }) }, { label: 'Test Run' }]);

  // Once a workflow run exists, hand off to the canonical run detail page.
  const redirectRunId = workflowRun?.id ?? null;
  React.useEffect(() => {
    if (redirectRunId) {
      navigate({
        to: '/runs/$runId',
        params: { runId: redirectRunId },
        search: { testRunId },
        replace: true,
      });
    }
  }, [redirectRunId, testRunId, navigate]);

  if (wrongOrgInfo) {
    return (
      <Page>
        <OrgSwitchPrompt
          resourceType="test run"
          targetOrgId={wrongOrgInfo.orgId}
          targetOrgName={wrongOrgInfo.orgName}
          redirectUrl={`/runs/tests/${testRunId}`}
          onCancel={() => {
            clearWrongOrgInfo();
            navigate({ to: '/runs' });
          }}
        />
      </Page>
    );
  }

  if (testRunQuery.isError || (!testRunQuery.isLoading && !testRun)) {
    return (
      <Page>
        <PageHeader
          title="Test Run Not Found"
          description="The requested test run could not be found"
        />
        <div className="border-2 border-dashed border-border rounded-lg py-12 text-center">
          <Text className="text-muted-foreground">
            This test run may have been deleted or you may not have access.
          </Text>
          <Button
            variant="outline"
            size="sm"
            onPress={() => navigate({ to: '/runs' })}
            className="mt-4"
          >
            Back to Runs
          </Button>
        </div>
      </Page>
    );
  }

  // A terminal test that never produced a run (e.g. skipped) has nothing to
  // open on the run detail page — show a brief note rather than spinning.
  const isPending =
    testRun?.status === 'queued' ||
    testRun?.status === 'running' ||
    testRun?.status === 'waiting';
  if (testRun && !workflowRun && !isPending) {
    const description =
      testRun.status === 'skipped' && testRun.skipReason
        ? `Skipped: ${testRun.skipReason}`
        : `This test run finished with status "${testRun.status}" and produced no recording to show.`;

    return (
      <Page>
        <EmptyState
          icon={<FilmSlate size={22} />}
          title="No run recording"
          description={description}
          action={
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" size="sm" onPress={() => navigate({ to: '/runs' })}>
                Back to Runs
              </Button>
              <Button
                variant="primary"
                size="sm"
                onPress={() =>
                  navigate({
                    to: '/flows/$workflowId',
                    params: { workflowId: testRun.workflowId },
                  })
                }
              >
                <FlowArrow size={16} />
                Go to flow
              </Button>
            </div>
          }
        />
      </Page>
    );
  }

  // Loading, or waiting for the workflow run to come up (then the effect above
  // redirects). Keep a spinner so the legacy surface never flashes.
  return (
    <Page>
      <div className="flex items-center justify-center py-12">
        <LoadingOrbit size="lg" />
      </div>
    </Page>
  );
};
